import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useEffect, useState } from 'react';
import useMonthNames from '@/helpers/useMonthNames';
import Layout from '@/content/Layouts';
import useCargoApi from '@/services/api/useCargoApi';
import useDate from '@/features/hooks/useDate';
import useAuth from '@/features/hooks/useAuth';
import useMonth from '@/features/hooks/useMonth';
import { useNavigate } from 'react-router-dom';
import TabNavigation from '@/components/TabNavigation';
import formatCurrency from '@/utils/format-currency';
import CountUp from 'react-countup';
import { renderIcon } from '@/utils/iconRenderer';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const options = {
  responsive: true,
  plugins: {
    legend: {
      position: 'top'
    },
    title: {
      display: true,
      text: 'Tarih - M3'
    }
  }
};

function CargoDashboard() {
  const { t } = useTranslation();
  const { GetCargoGonderiList, GetAnaCariBakiyeList, GetCargoBakiye } =
    useCargoApi();
  const [bakiyeList, setBakiyeList] = useState([]);
  const [kargoBakiye, setKargoBakiye] = useState(0);
  const [subAccount, setSubAccount] = useState(0);
  const [gonderiData, setGonderiData] = useState('');
  const { ilkTarih, sonTarih } = useDate();
  const { monthNames } = useMonthNames();
  const { user } = useAuth();
  const labels = useMonth();
  let navigate = useNavigate();

  useEffect(() => {
    async function getData() {
      const result = await GetCargoGonderiList(ilkTarih, sonTarih, subAccount);
      setGonderiData(result);
      const bakiyeList = await GetAnaCariBakiyeList(subAccount);
      setBakiyeList(bakiyeList);
      const kargoBakiyeResponse = await GetCargoBakiye(subAccount);
      setKargoBakiye(
        kargoBakiyeResponse?.[0]?.bakiye ? kargoBakiyeResponse[0].bakiye : 0
      );
    }
    if (ilkTarih && sonTarih && user) {
      getData();
    }
    return () => {
      setGonderiData('');
    };
  }, [ilkTarih, sonTarih, user, subAccount]);

  let toplamKap = 0;
  let toplamKg = 0;
  let toplamM3 = 0;
  const kapsayisi = [];
  const m3 = [];

  if (gonderiData) {
    gonderiData.forEach((el) => {
      const elDate = new Date(el.tarih);
      toplamKap += parseInt(el.kapSayisi);
      toplamKg += parseFloat(el.kg);
      toplamM3 += parseFloat(el.m3);
      let currentLabel = '';
      if (elDate.getFullYear() === new Date().getFullYear()) {
        currentLabel = monthNames[elDate.getMonth()];
      } else {
        currentLabel = monthNames[elDate.getMonth()] + elDate.getFullYear();
      }
      labels.forEach((label) => {
        if (label == currentLabel) {
          kapsayisi[labels.indexOf(label)] =
            (kapsayisi[labels.indexOf(label)] || 0) + parseInt(el.kapSayisi);
          m3[labels.indexOf(label)] =
            (m3[labels.indexOf(label)] || 0) + parseInt(el.m3);
        }
      });
    });
  }

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Kapsayısı',
        data: kapsayisi,
        backgroundColor: 'rgba(255, 99, 132, 0.5)'
      },
      {
        label: 'M3',
        data: m3,
        backgroundColor: 'rgba(53, 162, 235, 0.5)'
      }
    ]
  };

  return (
    <Layout>
      {user?.subAccount == 1 && (
        <TabNavigation
          activeTab={subAccount}
          setActiveTab={(value) => setSubAccount(value)}
        />
      )}
      <div className="container grid gap-5 md:grid-cols-3">
        {/* Havale Bakiye Kartı */}
        <div className="bg-color-bg rounded-lg shadow-md shadow-orange-200 border-spacing-1  m-2">
          <div className="border  border-gray-300  bg-color-card-header p-2  flex flex-row flex-nowrap items-center gap-2 ">
            {renderIcon('menu', 24, 'mx-2 text-gray-600')}
            <p className="text-lg font-semibold text-gray-700">
              {t('HavaleBakiye')}
            </p>
          </div>
          {bakiyeList?.map((el, i) => (
            <div
              key={i}
              className="border-b border-gray-200  bg-white shadow-sm">
              <div className=" bg-white p-1.5">
                <p className="text-gray-500 text-md font-normal">
                  {t(el.hesapAdi)}
                </p>
              </div>
              <div className="grid grid-cols-3 items-center content-center">
                <p className="pr-3 text-sm font-semibold text-center text-gray-500 ">
                  {formatCurrency(el?.bakiye)}
                </p>
                <p className="text-sm  text-gray-400">{el.doviz}</p>
                <button
                  className="bg-color-button text-gray-100 text-sm rounded hover:bg-color-button-hover transition shadow-lg p-1 flex items-center justify-start gap-2 m-2"
                  onClick={() => navigate(`/kargo/HavaleHesap/${el.kodu}`)}>
                  {renderIcon('detail', 22, 'mx-2 text-gray-100')}

                  {t('Detay')}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Depo Bakiye Kartı */}
        <div className="bg-white rounded-lg shadow-md shadow-blue-200 border-spacing-1  m-2">
          <div className="border-b  border-gray-300  bg-color-card-header p-2 flex flex-row flex-nowrap items-center gap-2  ">
            {renderIcon('menu', 24, 'mx-2 text-gray-600')}
            <p className="text-lg font-semibold text-gray-700">
              {t('DepoBakiye')}
            </p>
          </div>
          <div key={1} className="border-b border-gray-200  bg-white shadow-sm">
            <div className=" bg-white p-1.5">
              <p className="text-gray-500 text-md font-normal">
                {t('Balance')}
              </p>
            </div>
            <div className="grid grid-cols-3 items-center content-center">
              <p className="pr-3 text-sm font-semibold text-center text-gray-500 ">
                {formatCurrency(kargoBakiye)}
              </p>
              <p className="text-sm  text-gray-400">USD</p>
              <button
                className="bg-color-button text-gray-100 text-sm rounded hover:bg-color-button-hover transition shadow-lg p-1 flex items-center justify-start gap-2 m-2"
                onClick={() => navigate(`/kargo/KargoHesap/`)}>
                {renderIcon('detail', 22, 'mx-2 text-gray-100')}

                {t('Detay')}
              </button>
            </div>
          </div>
        </div>

        {/* Paket, Kg ve M3 Kartı */}

        <div className="bg-white rounded-lg shadow-md shadow-green-200 border-spacing-1  m-2">
          <div className="border-b  border-gray-300  bg-color-card-header p-2 flex flex-row flex-nowrap items-center gap-2">
            {renderIcon('menu', 24, 'mx-2 text-gray-600')}
            <p className="text-lg font-semibold text-gray-700">
              {t('PostSummary')}
            </p>
          </div>
          <div key={1} className="border-b border-gray-200  bg-white shadow-sm">
            <div className="bg-white grid grid-cols-2 border-spacing-1 border-gray-50 p-4 shadow-md">
              <div
                id="PaketSayisi"
                className="bg-white flex flex-row justify-start align-items-center flex-nowrap">
                {renderIcon('package', 32, 'mx-2 text-gray-500')}
                <p className="text-gray-400  text-right">{t('PaketSayisi')}</p>
              </div>
              <CountUp
                start={0}
                end={toplamKap}
                duration={2}
                className="text-xl  text-gray-600"
              />
            </div>
            <div className="grid grid-cols-2 border-spacing-1 border-gray-50 p-4 shadow-md">
              <div
                id="kg"
                className="bg-white flex flex-row justify-start align-items-center flex-nowrap">
                {renderIcon('kg', 32, 'mx-2 text-gray-500')}
                <p className="text-gray-400  text-right">{t('Kg')}</p>
              </div>
              <CountUp
                start={0}
                end={toplamKg}
                duration={2}
                className="text-xl  text-gray-600"
              />
            </div>
            <div className="grid grid-cols-2 border-spacing-1 border-gray-50 p-4 shadow-md">
              <div
                id="m3"
                className="bg-white flex flex-row justify-start align-items-center flex-nowrap">
                {renderIcon('m3', 32, 'mx-2 text-gray-500')}
                <p className="text-gray-400 text-right">{t('M3')}</p>
              </div>
              <CountUp
                start={0}
                end={toplamM3.toFixed(2)}
                duration={2}
                decimals={2}
                className="text-xl  text-gray-600"
              />
            </div>
          </div>
        </div>

        {/* M3 ve Kg Bar Grafiği Kartı */}
        <div className="col-span-full bg-white rounded-lg shadow-lg shadow-red-200-100 border-spacing-1  m-2">
          <div className="bg-color-primary text-white text-center py-2 ">
            {t('M3Kg')}
          </div>
          <div className="p-4">
            {gonderiData && <Bar options={options} data={chartData} />}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default CargoDashboard;
